type TokenKind = 'digit' | 'strong' | 'weak' | 'letter';

type ParsedToken = {
  index: number;
  prefix: string;
  core: string;
  suffix: string;
  kind: TokenKind | null;
  value: string | null;
};

const HEBREW_DIGITS: Record<string, string> = {
  'אפס': '0',
  'אחד': '1',
  'אחת': '1',
  'שתיים': '2',
  'שניים': '2',
  'שלוש': '3',
  'שלושה': '3',
  'ארבע': '4',
  'ארבעה': '4',
  'חמש': '5',
  'חמישה': '5',
  'שש': '6',
  'שישה': '6',
  'שבע': '7',
  'שבעה': '7',
  'שמונה': '8',
  'תשע': '9',
  'תשעה': '9',
};

const ENGLISH_STRONG_DIGITS: Record<string, string> = {
  zero: '0',
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  eight: '8',
  nine: '9',
};

const ENGLISH_WEAK_DIGITS: Record<string, string> = {
  oh: '0',
  o: '0',
  to: '2',
  too: '2',
  tow: '2',
};

const HEBREW_CODE_LETTERS: Record<string, string> = {
  '\u05d0': 'A', // Alef
  '\u05d1': 'B', // Bet
  '\u05d2': 'G', // Gimel
  '\u05d3': 'D', // Dalet
  '\u05d4': 'H', // He
  '\u05d5': 'V', // Vav
  '\u05d6': 'Z', // Zayin
  '\u05d7': 'H', // Het
  '\u05d8': 'T', // Tet
  '\u05d9': 'Y', // Yod
  '\u05db': 'K', // Kaf
  '\u05da': 'K', // Final Kaf
  '\u05dc': 'L', // Lamed
  '\u05de': 'M', // Mem
  '\u05dd': 'M', // Final Mem
  '\u05e0': 'N', // Nun
  '\u05df': 'N', // Final Nun
  '\u05e1': 'S', // Samekh
  '\u05e2': 'A', // Ayin
  '\u05e4': 'P', // Pe
  '\u05e3': 'P', // Final Pe
  '\u05e6': 'T', // Tsadi (mapped to T for Ts)
  '\u05e5': 'T', // Final Tsadi (mapped to T for Ts)
  '\u05e7': 'K', // Qof
  '\u05e8': 'R', // Resh
  '\u05e9': 'S', // Shin (mapped to S for simplicity)
  '\u05ea': 'T', // Tav
  // Common multi-letter forms (with Yod for soft sounds)
  '\u05db\u05d9': 'K',
  '\u05db\u05d9\u05d9': 'K',
  '\u05e7\u05d9': 'K',
  '\u05e7\u05d9\u05d9': 'K',
};

const ENGLISH_CODE_LETTERS: Record<string, string> = {
  a: 'A',
  b: 'B',
  c: 'C',
  d: 'D',
  e: 'E',
  f: 'F',
  g: 'G',
  h: 'H',
  i: 'I',
  j: 'J',
  k: 'K',
  l: 'L',
  m: 'M',
  n: 'N',
  o: 'O',
  p: 'P',
  q: 'Q',
  r: 'R',
  s: 'S',
  t: 'T',
  u: 'U',
  v: 'V',
  w: 'W',
  x: 'X',
  y: 'Y',
  z: 'Z',
  // Common spoken variants
  kay: 'K',
  key: 'K',
  kei: 'K',
  kaye: 'K',
  see: 'C',
  cee: 'C',
  bee: 'B',
  dee: 'D',
  eee: 'E',
  gee: 'G',
  jay: 'J',
  ell: 'L',
  em: 'M',
  en: 'N',
  pee: 'P',
  cue: 'Q',
  are: 'R',
  ess: 'S',
  tee: 'T',
  you: 'U',
  vee: 'V',
  doubleyou: 'W',
  ex: 'X',
  why: 'Y',
  zed: 'Z',
  zee: 'Z',
};

function parseToken(token: string, index: number): ParsedToken {
  const match = token.match(/^([^\p{L}\p{N}]*)((?:[\p{L}\p{N}])+)([^\p{L}\p{N}]*)$/u);
  if (!match) {
    return { index, prefix: '', core: token, suffix: '', kind: null, value: null };
  }

  const [, prefix, core, suffix] = match;
  const englishCore = core.toLowerCase();

  if (/^\d+$/.test(core)) {
    return { index, prefix, core, suffix, kind: 'digit', value: core };
  }

  if (Object.prototype.hasOwnProperty.call(HEBREW_DIGITS, core)) {
    return { index, prefix, core, suffix, kind: 'strong', value: HEBREW_DIGITS[core] };
  }

  if (Object.prototype.hasOwnProperty.call(ENGLISH_STRONG_DIGITS, englishCore)) {
    return { index, prefix, core, suffix, kind: 'strong', value: ENGLISH_STRONG_DIGITS[englishCore] };
  }

  if (Object.prototype.hasOwnProperty.call(ENGLISH_WEAK_DIGITS, englishCore)) {
    return { index, prefix, core, suffix, kind: 'weak', value: ENGLISH_WEAK_DIGITS[englishCore] };
  }

  if (Object.prototype.hasOwnProperty.call(HEBREW_CODE_LETTERS, core)) {
    return { index, prefix, core, suffix, kind: 'letter', value: HEBREW_CODE_LETTERS[core] };
  }

  if (Object.prototype.hasOwnProperty.call(ENGLISH_CODE_LETTERS, englishCore)) {
    return { index, prefix, core, suffix, kind: 'letter', value: ENGLISH_CODE_LETTERS[englishCore] };
  }

  return { index, prefix, core, suffix, kind: null, value: null };
}

function isWhitespaceOnly(value: string): boolean {
  return /^\s*$/.test(value);
}

function shouldConvertRun(run: ParsedToken[]): boolean {
  if (run.length < 2) return false;

  if (run[0].kind === 'letter') {
    const tail = run.slice(1);
    if (tail.length === 0) return false;

    const hasExplicitLetterPrefix = run[0].core.length === 1;
    if (run.length === 2 && !hasExplicitLetterPrefix) {
      return tail[0].kind === 'digit';
    }

    const hasStrongOrDigit = tail.some((token) => token.kind === 'strong' || token.kind === 'digit');
    if (hasStrongOrDigit) return true;

    const weakCount = tail.filter((token) => token.kind === 'weak').length;
    return weakCount >= 2;
  }

  const hasStrongOrDigit = run.some((token) => token.kind === 'strong' || token.kind === 'digit');
  if (!hasStrongOrDigit) return false;

  const hasWeak = run.some((token) => token.kind === 'weak');
  if (!hasWeak) return true;

  const hasDigit = run.some((token) => token.kind === 'digit');
  return hasDigit || run.length >= 3;
}

export function normalizeSpokenDigitSequences(text: string): string {
  if (!text.trim()) return text;

  const tokens = text.match(/\S+|\s+/g) ?? [text];
  const parsed = tokens
    .map((token, index) => parseToken(token, index))
    .filter((token) => !isWhitespaceOnly(tokens[token.index]));

  for (let i = 0; i < parsed.length; i++) {
    if (!parsed[i].kind) continue;

    let end = i;
    while (end + 1 < parsed.length) {
      const gap = tokens.slice(parsed[end].index + 1, parsed[end + 1].index);
      if (!gap.every(isWhitespaceOnly) || !parsed[end + 1].kind) break;
      end++;
    }

    const run = parsed.slice(i, end + 1);
    if (shouldConvertRun(run)) {
      const replacement = `${run[0].prefix}${run.map((token) => token.value ?? '').join('')}${run[run.length - 1].suffix}`;
      tokens[run[0].index] = replacement;
      for (let j = run[0].index + 1; j <= run[run.length - 1].index; j++) {
        tokens[j] = '';
      }
    }

    i = end;
  }

  return tokens.join('');
}
